import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, Users, BedDouble, Bath, MapPin, Check, MessageCircle, CalendarDays, ExternalLink } from "lucide-react";
import { useSettings } from "@/components/SocialLinks";
import { GoogleReviews } from "@/components/GoogleReviews";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const settings = useSettings();
  const [listing, setListing] = useState(null);
  const [range, setRange] = useState(undefined);
  const [guests, setGuests] = useState(1);
  const [busy, setBusy] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [booked, setBooked] = useState([]);
  const [replyText, setReplyText] = useState({});
  const isStaff = user && (user.role === "host" || user.role === "admin");

  const loadReviews = () => api.get(`/listings/${id}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setListing(data)).catch(() => navigate("/"));
    loadReviews();
    api.get(`/listings/${id}/availability`).then(({ data }) => setBooked(data)).catch(() => {});
  }, [id, navigate]);

  if (!listing) return <div className="min-h-screen bg-background"><Header /></div>;

  const nights = range?.from && range?.to ? Math.max(Math.round((range.to - range.from) / 86400000), 1) : 0;
  const subtotal = nights * listing.price_per_night;
  const fees = Math.round(subtotal * 0.08);
  const total = subtotal + fees;
  const disabledDays = [{ before: new Date() }, ...booked.map((b) => ({ from: new Date(b.check_in), to: new Date(b.check_out) }))];

  const requireAuth = () => {
    if (!user || !user.role) {
      toast.error("Connectez-vous pour continuer");
      navigate("/connexion");
      return false;
    }
    return true;
  };

  const handleBooking = async () => {
    if (!requireAuth()) return;
    if (!range?.from || !range?.to) return toast.error("Choisissez vos dates de séjour");
    setBusy(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        listing_id: listing.listing_id,
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        guests: Number(guests),
      });
      const { data } = await api.post("/payments/checkout", {
        booking_id: booking.booking_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const submitReview = async () => {
    if (!requireAuth()) return;
    if (!myRating) return toast.error("Choisissez une note");
    try {
      await api.post(`/listings/${id}/reviews`, { rating: myRating, comment: myComment });
      toast.success("Merci pour votre avis !");
      setMyComment(""); setMyRating(0);
      loadReviews();
      api.get(`/listings/${id}`).then(({ data }) => setListing(data));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitReply = async (reviewId) => {
    try {
      await api.post(`/listings/${id}/reviews/${reviewId}/reply`, { reply: replyText[reviewId] || "" });
      toast.success("Réponse publiée");
      setReplyText({ ...replyText, [reviewId]: "" });
      loadReviews();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleContact = async () => {    if (!requireAuth()) return;
    try {
      await api.post("/conversations", {
        host_id: listing.host_id,
        listing_id: listing.listing_id,
        subject: `À propos de ${listing.title}`,
      });
      toast.success("Conversation créée avec l'hôte");
      navigate("/messagerie");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
        <button onClick={() => navigate("/")} className="text-sm text-muted-foreground hover:text-olive mb-4" data-testid="back-link">← Retour</button>
        <h1 className="font-serif text-3xl md:text-4xl text-olive">{listing.title}</h1>
        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
          <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-rose-brand" /> {listing.location}</span>
          <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-rose-brand text-rose-brand" /> {listing.rating ? `${String(listing.rating).replace(".", ",")} · ${listing.review_count} avis` : "Nouveau"}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6 rounded-2xl overflow-hidden">
          {(listing.images || []).slice(0, 2).map((img, i) => (
            <img key={i} src={img} alt="" className="w-full h-64 md:h-80 object-cover" />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-10">
          <div className="lg:col-span-2">
            <div className="flex gap-6 pb-6 border-b border-border text-sm">
              <span className="flex items-center gap-2"><Users className="w-5 h-5 text-olive" /> {listing.max_guests} voyageurs</span>
              <span className="flex items-center gap-2"><BedDouble className="w-5 h-5 text-olive" /> {listing.bedrooms} chambres</span>
              <span className="flex items-center gap-2"><Bath className="w-5 h-5 text-olive" /> {listing.bathrooms} SdB</span>
            </div>
            <h2 className="font-serif text-2xl text-olive mt-6 mb-3">À propos de ce logement</h2>
            <p className="text-foreground leading-relaxed">{listing.description}</p>

            <h2 className="font-serif text-2xl text-olive mt-8 mb-3">Équipements</h2>
            <div className="grid grid-cols-2 gap-3">
              {(listing.amenities || []).map((a) => (
                <span key={a} className="flex items-center gap-2 text-foreground"><Check className="w-4 h-4 text-rose-brand" /> {a}</span>
              ))}
            </div>

            {listing.host && (
              <div className="mt-8 p-5 rounded-2xl bg-secondary flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Votre hôte</p>
                  <p className="font-serif text-xl text-olive">{listing.host.name}</p>
                </div>
                <Button variant="outline" onClick={handleContact} className="border-olive text-olive" data-testid="contact-host-button">
                  <MessageCircle className="w-4 h-4 mr-2" /> Contacter
                </Button>
              </div>
            )}

            <div className="mt-10" id="avis" data-testid="reviews-section">
              <div className="mb-8"><GoogleReviews compact /></div>
              <h2 className="font-serif text-2xl text-olive mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 fill-rose-brand text-rose-brand" />
                {listing.rating ? `${String(listing.rating).replace(".", ",")} · ${listing.review_count} avis` : "Aucun avis pour le moment"}
              </h2>
              {settings?.home?.google_reviews_url && (
                <a href={settings.home.google_reviews_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-rose-brand font-semibold hover:underline mb-4" data-testid="google-reviews-link">
                  <ExternalLink className="w-4 h-4" /> Voir aussi nos avis Google
                </a>
              )}

              <div className="space-y-4 mb-8">
                {reviews.map((r) => (
                  <div key={r.review_id} className="p-4 rounded-2xl bg-secondary" data-testid={`review-${r.review_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-olive">{r.author_name}</span>
                      <span className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-rose-brand text-rose-brand" : "text-border"}`} />
                        ))}
                      </span>
                    </div>
                    {r.comment && <p className="text-sm text-foreground mt-2">{r.comment}</p>}
                    {r.reply ? (
                      <div className="mt-3 pl-3 border-l-2 border-olive" data-testid={`review-reply-${r.review_id}`}>
                        <p className="text-xs font-semibold text-olive">Réponse de {r.reply_author || "l'hôte"}</p>
                        <p className="text-sm text-foreground">{r.reply}</p>
                      </div>
                    ) : isStaff ? (
                      <div className="mt-3 flex gap-2">
                        <Input value={replyText[r.review_id] || ""} onChange={(e) => setReplyText({ ...replyText, [r.review_id]: e.target.value })}
                          placeholder="Répondre publiquement..." className="h-9" data-testid={`reply-input-${r.review_id}`} />
                        <Button size="sm" onClick={() => submitReply(r.review_id)} className="bg-olive hover:bg-olive-dark text-white shrink-0" data-testid={`reply-submit-${r.review_id}`}>Répondre</Button>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="p-5 rounded-2xl border border-border">
                <p className="font-serif text-lg text-olive mb-3">Laissez votre avis</p>
                <div className="flex items-center gap-1 mb-3" data-testid="review-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setMyRating(n)} data-testid={`review-star-${n}`}>
                      <Star className={`w-7 h-7 transition-colors ${n <= myRating ? "fill-rose-brand text-rose-brand" : "text-border hover:text-rosebrand-soft"}`} />
                    </button>
                  ))}
                </div>
                <Textarea value={myComment} onChange={(e) => setMyComment(e.target.value)} placeholder="Partagez votre expérience..." data-testid="review-comment" />
                <Button onClick={submitReview} className="mt-3 bg-olive hover:bg-olive-dark text-white" data-testid="submit-review-button">
                  Publier mon avis
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-2xl border border-border bg-white p-6 shadow-lg" data-testid="booking-card">
              <p className="mb-4">
                <span className="font-bold text-2xl text-olive">{listing.price_per_night} €</span>
                <span className="text-muted-foreground"> / nuit</span>
              </p>

              <Popover>
                <PopoverTrigger asChild>
                  <button className="w-full flex items-center gap-2 border border-border rounded-xl px-4 py-3 text-left text-sm hover:border-olive transition-colors" data-testid="date-picker-trigger">
                    <CalendarDays className="w-4 h-4 text-olive" />
                    {range?.from && range?.to
                      ? `${format(range.from, "d MMM", { locale: fr })} → ${format(range.to, "d MMM", { locale: fr })}`
                      : "Choisir les dates"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} locale={fr} disabled={disabledDays} />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground mt-1" data-testid="calendar-legend">Les dates grisées sont déjà réservées (Lodgify inclus).</p>

              <div className="flex items-center gap-2 border border-border rounded-xl px-4 py-2 mt-3">
                <Users className="w-4 h-4 text-olive" />
                <Input type="number" min="1" max={listing.max_guests} value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  className="border-0 shadow-none focus-visible:ring-0 px-0" data-testid="guests-input" />
              </div>

              {nights > 0 && (
                <div className="mt-4 space-y-2 text-sm" data-testid="price-breakdown">
                  <div className="flex justify-between text-muted-foreground">
                    <span>{listing.price_per_night} € × {nights} nuit(s)</span><span>{subtotal} €</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Frais de service</span><span>{fees} €</span>
                  </div>
                  <div className="flex justify-between font-bold text-olive pt-2 border-t border-border">
                    <span>Total</span><span>{total} €</span>
                  </div>
                </div>
              )}

              <Button onClick={handleBooking} disabled={busy}
                className="w-full mt-5 bg-rose-brand hover:bg-rosebrand-soft text-white rounded-full h-12" data-testid="booking-submit-button">
                {busy ? "Redirection..." : "Réserver et payer"}
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-2">Paiement sécurisé via Stripe</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
